import React, { useState } from 'react';
import { CourseData, ExcludedItems } from '../../types/CourseData';
import CanvasCourseTab from "../../components/CanvasCourseTab";
import ToggleSwitch from "../../components/ToggleSwitch";
import "../../styles/loader.css";

type VisibilityKey = 'announcements' | 'assignments' | 'discussions' | 'files' | 'modules' | 'pages' | 'enrollments' | 'quizzes' | 'syllabus';

interface PreviewTabProps {
  selectedCourseData: CourseData | undefined;
  isLoadingTabs: { [courseId: number]: { [key: string]: boolean } };
  handleToggleChange: (key: VisibilityKey, courseId: number) => void;
  handleCheckboxChange: (itemId: string, itemType: string, courseId: number, idField: string) => void;
  checkedItems: string[];
  updateExcludedItems: (courseId: number, type: keyof ExcludedItems, itemId: string) => void;
}

const PreviewTab: React.FC<PreviewTabProps> = ({
  selectedCourseData,
  isLoadingTabs,
  handleToggleChange,
  handleCheckboxChange,
  checkedItems,
  updateExcludedItems
}) => {
  const [activeSubTab, setActiveSubTab] = useState<VisibilityKey>('announcements');

  const handleTabChange = (key: string) => {
    setActiveSubTab(key as VisibilityKey);
  };

  const handleInsideToggleChange = (key: VisibilityKey, courseId: number) => {
    handleToggleChange(key, courseId);
  };

  if (!selectedCourseData) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-gray-500">No course selected</p>
      </div>
    );
  }

  const contentTypes: VisibilityKey[] = [
    'announcements', 
    'assignments', 
    'discussions', 
    'files', 
    'modules', 
    'pages', 
    'enrollments', 
    'quizzes', 
    'syllabus'
  ];

  return (
    <div className="h-full">
      <div className="flex border rounded-lg p-4">
        {/* Left Navigation */}
        <div className="w-[17%] p-2 max-h-80 h-screen sticky top-0 overflow-y-auto border-r">
          <ul className="flex flex-col space-y-3">
            {contentTypes.map((tab) => (
              <li
                key={tab}
                className={`cursor-pointer px-6 py-2 rounded-l-lg transition-all duration-300 ease-in-out flex items-center border-r-4 ${
                  activeSubTab === tab
                    ? "border-black text-black font-semibold bg-gradient-to-r from-gray-200 to-gray-100 shadow-md"
                    : "text-gray-700 hover:bg-gray-100 hover:text-black hover:border-r-4 hover:border-gray-500"
                }`}
                onClick={() => handleTabChange(tab)}
              >
                {tab.charAt(0).toUpperCase() + tab.slice(1)}
              </li>
            ))}
          </ul>
        </div>

        {/* Right Content Area */}
        <div className="w-[83%] pl-4 overflow-y-auto">
          {selectedCourseData.error ? (
            <div className="text-red-600 text-center py-4">
              {selectedCourseData.error}
            </div>
          ) : (
            <>
              {contentTypes.map((tab) => {
                const items = (selectedCourseData?.courseItems as { [key: string]: any[] })?.[tab] ?? [];
                const hasData = items.length > 0;
                const displayName = tab.charAt(0).toUpperCase() + tab.slice(1);
                const ismatchSubTab = activeSubTab === tab;
                const matchSubTab = activeSubTab.charAt(0).toUpperCase() + activeSubTab.slice(1);
                const courseId = selectedCourseData.id;
                const isVisible = selectedCourseData.visibility[tab as keyof typeof selectedCourseData.visibility];
                const isTabLoading = isLoadingTabs[courseId]?.[tab];

                return (
                  <div className="w-full max-h-80 overflow-y-auto" key={tab}>
                    {ismatchSubTab ? (
                      <>
                        {isTabLoading ? (
                          <div className="flex justify-center items-center h-20">
                            <div className="dotted-circle">
                              {[...Array(10)].map((_, i) => (
                                <span 
                                  key={i} 
                                  className="dot" 
                                  style={{ "--i": i } as React.CSSProperties}
                                ></span>
                              ))}
                            </div>
                          </div>
                        ) : (
                          <>
                            {hasData ? (
                              <>
                                <ToggleSwitch
                                  label={displayName}
                                  visibility={selectedCourseData.visibility}
                                  handleToggleChange={(key: VisibilityKey) =>
                                    handleInsideToggleChange(key, courseId)
                                  }
                                  key={tab}
                                  tab={tab as VisibilityKey}
                                  courseId={courseId}
                                />
                                <div className={`transition-opacity duration-300 ${isVisible ? '' : 'opacity-50 pointer-events-none'}`}>
                                  <CanvasCourseTab
                                    activeSubTab={activeSubTab}
                                    displayName={displayName}
                                    matchSubTab={matchSubTab}
                                    visibility={selectedCourseData.visibility}
                                    items={items}
                                    itemCount={items.length}
                                    handleTabChange={handleTabChange}
                                    handleCheckboxChange={handleCheckboxChange}
                                    checkedItems={checkedItems}
                                    courseId={Array.isArray(courseId) ? 0 : courseId ?? 0}
                                    excludedItems={selectedCourseData.excludedItems}
                                    updateExcludedItems={updateExcludedItems as (courseId: number, type: string, itemId: string) => void}
                                    selectedItems={selectedCourseData.selectedItems}
                                  />
                                </div>
                              </>
                            ) : (
                              <div className="flex items-center justify-center h-full w-full text-gray-500">
                                <h3 className="text-xl font-semibold mb-4">No data found</h3>
                              </div>
                            )}
                          </>
                        )}
                      </>
                    ) : null}
                  </div>
                );
              })}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default PreviewTab;